import { X } from 'lucide-react';
import { useState } from 'react';
import { BLOCK_LABELS, getSocialNetworkMeta, SOCIAL_NETWORK_OPTIONS } from '../../blocks/blockLibrary';
import type { BlockPatch, EmailBlock } from '../../HtmlEditor.types';
import { ColorRow, Field, Quad, SegGroup } from './inspectorFields';

type InspectorProps = {
  block: EmailBlock;
  onChange: (patch: BlockPatch) => void;
  onClose: () => void;
  disabled?: boolean;
};

export function Inspector({ block: b, onChange, onClose, disabled }: InspectorProps) {
  const [grouped, setGrouped] = useState(true);

  return (
    <div className='ee-insp'>
      <div className='ee-insp__header'>
        <div>
          <div className='ee-insp__crumbs'>Block</div>
          <div className='ee-insp__title-h'>{BLOCK_LABELS[b.type] || b.type}</div>
        </div>
        <button type='button' className='ee-iconbtn' onClick={onClose} title='Close' disabled={disabled}>
          <X size={14} />
        </button>
      </div>

      {(b.type === 'button' || b.type === 'dynamic' || b.type === 'image' || b.type === 'html' || b.type === 'social') && (
        <div className='ee-insp__section'>
          <div className='ee-insp__title'>Content</div>
          {b.type === 'button' && (
            <>
              <Field label='Label'>
                <input className='input' value={b.text} disabled={disabled} onChange={(e) => onChange({ text: e.target.value })} />
              </Field>
              <Field label='URL'>
                <input className='input' value={b.href} disabled={disabled} onChange={(e) => onChange({ href: e.target.value })} />
              </Field>
              <ColorRow label='Button color' value={b.btnBg} disabled={disabled} onChange={(v) => onChange({ btnBg: v })} />
              <ColorRow label='Text color' value={b.btnColor} disabled={disabled} onChange={(v) => onChange({ btnColor: v })} />
              <Field label='Border radius'>
                <input className='input' type='number' value={b.radius} disabled={disabled} onChange={(e) => onChange({ radius: +e.target.value })} />
              </Field>
            </>
          )}
          {b.type === 'dynamic' && (
            <>
              <Field label='Source'>
                <select className='select' value={b.source} disabled={disabled} onChange={(e) => onChange({ source: e.target.value })}>
                  <option value='audience.first_name'>audience.first_name</option>
                  <option value='audience.last_name'>audience.last_name</option>
                  <option value='audience.city'>audience.city</option>
                  <option value='audience.email'>audience.email</option>
                  <option value='audience.discount'>audience.discount</option>
                  <option value='audience.expiry'>audience.expiry</option>
                </select>
              </Field>
              <Field label='Fallback'>
                <input className='input' value={b.fallback} disabled={disabled} onChange={(e) => onChange({ fallback: e.target.value })} />
              </Field>
            </>
          )}
          {b.type === 'image' && (
            <>
              <div className='ee-uploader'>
                <div className='ee-uploader__icon'>⬆</div>
                <div className='ee-uploader__main'>Drag & drop or click to upload</div>
                <div className='ee-uploader__hint'>JPG, PNG, GIF · max 5MB · 600 px wide recommended</div>
              </div>
              <Field label='Image URL'>
                <input className='input' placeholder='https://…' value={b.src} disabled={disabled} onChange={(e) => onChange({ src: e.target.value })} />
              </Field>
              <Field label='Height (px)'>
                <input className='input' type='number' value={b.height} disabled={disabled} onChange={(e) => onChange({ height: +e.target.value })} />
              </Field>
            </>
          )}
          {b.type === 'html' && (
            <Field label='HTML code'>
              <textarea
                className='input'
                rows={6}
                value={b.code}
                disabled={disabled}
                onChange={(e) => onChange({ code: e.target.value })}
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
              />
            </Field>
          )}
          {b.type === 'social' && (
            <>
              {b.links.length === 0 && (
                <div className='ee-insp__sublabel' style={{ marginBottom: 12 }}>
                  No networks added. Use the dropdown below to add one.
                </div>
              )}
              {b.links.map((link, index) => {
                const label = getSocialNetworkMeta(link.network)?.label ?? link.network;
                return (
                  <div key={`${link.network}-${index}`} style={{ marginBottom: 12 }}>
                    <Field label={`${label} URL`}>
                      <div className='ee-fieldrow'>
                        <input
                          className='input'
                          placeholder='https://…'
                          value={link.url}
                          disabled={disabled}
                          onChange={(e) => {
                            const links = b.links.map((item, i) =>
                              i === index ? { ...item, url: e.target.value } : item
                            );
                            onChange({ links });
                          }}
                        />
                        <button
                          type='button'
                          className='ee-iconbtn'
                          title={`Remove ${label}`}
                          disabled={disabled}
                          onClick={() => onChange({ links: b.links.filter((_, i) => i !== index) })}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </Field>
                  </div>
                );
              })}
              {SOCIAL_NETWORK_OPTIONS.some((option) => !b.links.some((link) => link.network === option.id)) && (
                <Field label='Add network'>
                  <select
                    className='select'
                    value=''
                    disabled={disabled}
                    onChange={(e) => {
                      const network = e.target.value;
                      if (!network) return;
                      onChange({ links: [...b.links, { network, url: '' }] });
                    }}
                  >
                    <option value=''>Select a network…</option>
                    {SOCIAL_NETWORK_OPTIONS.filter((option) => !b.links.some((link) => link.network === option.id)).map(
                      (option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </Field>
              )}
            </>
          )}
        </div>
      )}

      <div className='ee-insp__section'>
        <div className='ee-insp__title'>Layout</div>
        <Field label='Width'>
          <div className='ee-fieldrow'>
            <input className='input' type='number' value={b.width} disabled={disabled} onChange={(e) => onChange({ width: +e.target.value })} />
            <div className='ee-unit'>%</div>
          </div>
        </Field>
        <Field label='Block alignment'>
          <SegGroup
            value={b.align}
            disabled={disabled}
            onChange={(v) => onChange({ align: v as EmailBlock['align'] })}
            options={[
              { v: 'left', label: '⇤' },
              { v: 'center', label: '⇔' },
              { v: 'right', label: '⇥' },
            ]}
          />
        </Field>
        {(b.type === 'title' || b.type === 'text' || b.type === 'logo') && (
          <>
            <Field label='Text alignment'>
              <SegGroup
                value={b.align}
                disabled={disabled}
                onChange={(v) => onChange({ align: v as EmailBlock['align'] })}
                options={[
                  { v: 'left', label: '⇤' },
                  { v: 'center', label: '⇔' },
                  { v: 'right', label: '⇥' },
                  { v: 'justify', label: '☷' },
                ]}
              />
            </Field>
            {'lineHeight' in b && (
              <Field label='Line height'>
                <input
                  className='input'
                  type='number'
                  step='0.1'
                  value={b.lineHeight}
                  disabled={disabled}
                  onChange={(e) => onChange({ lineHeight: +e.target.value })}
                />
              </Field>
            )}
          </>
        )}
      </div>

      <div className='ee-insp__section'>
        <div className='ee-insp__title-row'>
          <div className='ee-insp__title'>Spacing</div>
          <label className='ee-grouptoggle'>
            <input type='checkbox' checked={grouped} disabled={disabled} onChange={(e) => setGrouped(e.target.checked)} />
            <span>Grouped</span>
          </label>
        </div>
        <div className='ee-insp__sublabel'>Padding (text to border)</div>
        <Quad
          grouped={grouped}
          disabled={disabled}
          values={[b.paddingTop, b.paddingRight, b.paddingBottom, b.paddingLeft]}
          onChange={(t, r, bot, l) => onChange({ paddingTop: t, paddingRight: r, paddingBottom: bot, paddingLeft: l })}
        />
        <div className='ee-insp__sublabel' style={{ marginTop: 12 }}>
          Margin (block to block)
        </div>
        <Quad
          grouped={grouped}
          disabled={disabled}
          values={[b.marginTop, b.marginRight, b.marginBottom, b.marginLeft]}
          onChange={(t, r, bot, l) => onChange({ marginTop: t, marginRight: r, marginBottom: bot, marginLeft: l })}
        />
      </div>

      <div className='ee-insp__section'>
        <div className='ee-insp__title'>Background</div>
        <ColorRow label='Color' value={b.bg === 'transparent' ? '#FFFFFF' : b.bg} disabled={disabled} onChange={(v) => onChange({ bg: v })} />
        {/* <div className='ee-uploader'>
          <div className='ee-uploader__icon'>🖼</div>
          <div className='ee-uploader__main'>Add background image</div>
          <div className='ee-uploader__hint'>Drag & drop or click to upload</div>
        </div>
        <Field label='Image URL'>
          <input
            className='input'
            placeholder='https://…'
            value={b.bgImage}
            disabled={disabled}
            onChange={(e) => onChange({ bgImage: e.target.value })}
          />
        </Field> */}
      </div>
    </div>
  );
}
